import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import PublicNavbar from "../components/PublicNavbar";
import Footer from "../components/Footer";
import {
  Shield, Server, Activity, Bell, FileText, Eye,
  ArrowRight, CheckCircle, Monitor, HardDrive, Users, Zap
} from "lucide-react";

const HERO_BG = "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=1920&q=80";

const stats = [
  { value: "99.9%", label: "Uptime Monitoring" },
  { value: "< 30s", label: "Alerte en temps reel" },
  { value: "24/7", label: "Surveillance continue" },
  { value: "100+", label: "Serveurs geres" },
];

const features = [
  { icon: Monitor, title: "Dashboard temps reel", desc: "Visualisez l'etat de toutes vos sauvegardes Veeam et Iperius en un coup d'oeil depuis un tableau de bord centralise.", color: "text-cyan-400", bg: "bg-cyan-500/10 border-cyan-500/20" },
  { icon: Bell, title: "Alertes instantanees", desc: "Recevez des notifications par email des qu'une sauvegarde echoue ou presente un avertissement.", color: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
  { icon: Server, title: "Agents legers", desc: "Deployez un simple script PowerShell sur vos serveurs Windows. Aucune installation complexe requise.", color: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
  { icon: Eye, title: "Monitoring public", desc: "Generez un lien de suivi unique pour chaque client. Ils peuvent verifier leurs sauvegardes sans se connecter.", color: "text-indigo-400", bg: "bg-indigo-500/10 border-indigo-500/20" },
  { icon: FileText, title: "Rapports PDF & CSV", desc: "Exportez des rapports detailles par serveur ou par client en un clic. Ideal pour vos revues mensuelles.", color: "text-rose-400", bg: "bg-rose-500/10 border-rose-500/20" },
  { icon: Users, title: "Multi-tenant", desc: "Chaque utilisateur dispose de son espace isole. Gerez vos clients et serveurs en toute securite.", color: "text-purple-400", bg: "bg-purple-500/10 border-purple-500/20" },
];

const testimonials = [
  { name: "Thomas L.", role: "Directeur IT, MSP France", text: "ServeurOnline nous a permis de passer de la gestion manuelle des logs Veeam a un monitoring centralise en moins d'une heure." },
  { name: "Sophie M.", role: "Administratrice systemes", text: "Le mode monitoring public est genial pour nos clients. Ils voient l'etat de leurs sauvegardes sans nous appeler." },
  { name: "Marc D.", role: "Gerant MSP Belgique", text: "Les rapports PDF automatiques nous font gagner un temps considerable lors des revues trimestrielles avec nos clients." },
];

const Home = () => {
  return (
    <div className="min-h-screen bg-[#07090E]">
      <PublicNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Background */}
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#07090E]/60 via-[#07090E]/80 to-[#07090E]" />
          <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(6, 182, 212, 0.15), transparent)" }} />
        </div>

        {/* Grid overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 mb-6">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-mono text-cyan-400 tracking-wider uppercase">Monitoring actif 24/7</span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Vos sauvegardes,{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-emerald-400">
                  sous controle
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-8 max-w-lg">
                Centralisez le monitoring de vos sauvegardes Veeam et Iperius.
                Alertes en temps reel, rapports automatiques, et visibilite client.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button
                    className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-6 text-base rounded-xl shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-all"
                    data-testid="hero-cta-register"
                  >
                    Essai gratuit 14 jours
                    <ArrowRight size={18} className="ml-2" />
                  </Button>
                </Link>
                <Link to="/features">
                  <Button
                    variant="ghost"
                    className="text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 px-8 py-6 text-base rounded-xl"
                    data-testid="hero-cta-features"
                  >
                    Decouvrir les fonctionnalites
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right side - simulated NOC widget */}
            <div className="hidden lg:block">
              <div className="rounded-2xl border border-slate-800/60 bg-[#0D111A]/70 backdrop-blur-xl p-6 shadow-2xl shadow-cyan-950/20">
                <div className="flex items-center gap-3 mb-5">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-500/80" />
                    <div className="w-3 h-3 rounded-full bg-amber-500/80" />
                    <div className="w-3 h-3 rounded-full bg-emerald-500/80" />
                  </div>
                  <span className="text-xs text-slate-500 font-mono">ServeurOnline — Dashboard</span>
                </div>

                {[
                  { name: "SRV-DC01", client: "Dupont IT", agent: "Veeam", status: "success", time: "02:30" },
                  { name: "SRV-FILE02", client: "Martin Corp", agent: "Veeam", status: "success", time: "03:15" },
                  { name: "SRV-SQL01", client: "Lefevre SAS", agent: "Iperius", status: "warning", time: "04:00" },
                  { name: "SRV-WEB03", client: "Dupont IT", agent: "Veeam", status: "success", time: "01:45" },
                  { name: "SRV-MAIL01", client: "Bernard SARL", agent: "Iperius", status: "error", time: "05:20" },
                ].map((srv, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-3 border-b border-slate-800/40 last:border-0 group"
                    style={{ animationDelay: `${i * 100}ms` }}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${srv.status === "success" ? "bg-emerald-500" : srv.status === "warning" ? "bg-amber-500" : "bg-red-500"}`}>
                        {srv.status === "success" && <div className="w-2 h-2 rounded-full bg-emerald-500 animate-ping opacity-50" />}
                      </div>
                      <div>
                        <p className="text-sm text-white font-mono">{srv.name}</p>
                        <p className="text-xs text-slate-500">{srv.client}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-xs px-2 py-0.5 rounded font-mono ${
                        srv.agent === "Veeam" ? "bg-cyan-500/10 text-cyan-400" : "bg-purple-500/10 text-purple-400"
                      }`}>{srv.agent}</span>
                      <span className={`text-xs font-semibold ${
                        srv.status === "success" ? "text-emerald-400" : srv.status === "warning" ? "text-amber-400" : "text-red-400"
                      }`}>{srv.status.toUpperCase()}</span>
                      <span className="text-xs text-slate-600 font-mono">{srv.time}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="border-y border-slate-800/50 bg-[#0D111A]/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-white mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{stat.value}</p>
                <p className="text-xs sm:text-sm text-slate-500 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 sm:py-28" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-3">Fonctionnalites</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Tout ce dont vous avez besoin pour surveiller vos sauvegardes
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feat, i) => (
              <div
                key={i}
                className={`group p-6 rounded-2xl border ${feat.bg} hover:border-opacity-50 transition-all duration-300 hover:-translate-y-1`}
                data-testid={`feature-card-${i}`}
              >
                <div className={`w-12 h-12 rounded-xl ${feat.bg} flex items-center justify-center mb-4`}>
                  <feat.icon className={feat.color} size={24} />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feat.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 sm:py-28 bg-[#0D111A]/30" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-emerald-400/80 mb-3">Simplicite</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Operationnel en 3 etapes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Creez votre compte", desc: "Inscrivez-vous et configurez vos clients en quelques clics. Essai gratuit de 14 jours, sans carte bancaire.", icon: Users },
              { step: "02", title: "Deployez les agents", desc: "Telechargez et executez un simple script PowerShell sur vos serveurs. La configuration est automatique.", icon: HardDrive },
              { step: "03", title: "Surveillez tout", desc: "Retrouvez l'etat de toutes les sauvegardes dans votre dashboard. Recevez des alertes en cas de probleme.", icon: Activity },
            ].map((item, i) => (
              <div key={i} className="relative p-8 rounded-2xl border border-slate-800/60 bg-[#0D111A]/70">
                <span className="text-5xl font-bold text-slate-800/50 absolute top-4 right-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{item.step}</span>
                <item.icon className="text-cyan-400 mb-4" size={28} />
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 sm:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-3">Temoignages</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Ce que disent nos utilisateurs
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="p-6 rounded-2xl border border-slate-800/60 bg-[#0D111A]/70" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <span key={j} className="text-amber-400">&#9733;</span>
                  ))}
                </div>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">"{t.text}"</p>
                <div>
                  <p className="text-white font-semibold text-sm">{t.name}</p>
                  <p className="text-slate-500 text-xs">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="p-10 sm:p-16 rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-[#0D111A] to-[#0D1520]" style={{ boxShadow: "0 0 80px rgba(6, 182, 212, 0.08)" }}>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Pret a securiser vos sauvegardes ?
            </h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Rejoignez les MSPs qui font confiance a ServeurOnline pour le monitoring de leurs sauvegardes. Essai gratuit de 14 jours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-8 py-6 text-base rounded-xl shadow-lg shadow-cyan-500/20" data-testid="cta-register-btn">
                  Commencer gratuitement
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="ghost" className="text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 px-8 py-6 text-base rounded-xl" data-testid="cta-contact-btn">
                  Nous contacter
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
